/**
 * Türkçe harf işlemleri — projedeki TEK doğruluk kaynağı.
 *
 * JS'in yerleşik `toUpperCase()`'i Türkçe için bozuktur:
 *   "istanbul".toUpperCase() -> "ISTANBUL"  (olması gereken: "İSTANBUL")
 *   "ılık".toUpperCase()     -> "ILIK"      (i ve ı ayrımı kaybolur)
 *
 * `toLocaleUpperCase("tr")` doğru sonucu verir ama çalışma ortamının ICU
 * derlemesine bağımlıdır. Oyunun tüm karşılaştırmaları harf eşitliğine
 * dayandığı için burada kendi eşleme tablomuzu kullanıyoruz.
 */

/** Türk alfabesi, 29 harf, alfabetik sırada (büyük). */
export const ALPHABET = "ABCÇDEFGĞHIİJKLMNOÖPRSŞTUÜVYZ";

/** Türk alfabesi, 29 harf, alfabetik sırada (küçük). ALPHABET ile birebir hizalı. */
export const ALPHABET_LOWER = "abcçdefgğhıijklmnoöprsştuüvyz";

const TO_UPPER = new Map<string, string>();
const TO_LOWER = new Map<string, string>();
const ORDER = new Map<string, number>();

for (let i = 0; i < ALPHABET.length; i++) {
  const upper = ALPHABET[i]!;
  const lower = ALPHABET_LOWER[i]!;
  TO_UPPER.set(lower, upper);
  TO_UPPER.set(upper, upper);
  TO_LOWER.set(upper, lower);
  TO_LOWER.set(lower, lower);
  ORDER.set(upper, i);
}

/** Alfabedeki büyük harflerin kümesi — hızlı geçerlilik kontrolü için. */
export const ALPHABET_SET: ReadonlySet<string> = new Set(ALPHABET.split(""));

/**
 * Şapkalı ünlüler (â, î, û) Türk alfabesinin 29 harfi arasında değildir; TDK
 * bunları yalnızca uzunluk ve incelik ayrımı için yazar: "kâğıt", "imkân",
 * "sükût". Oyun 29 harfle oynandığı ve klavyede şapkalı tuş bulunmadığı için
 * kanonik kelime biçiminde taban harfe indirilirler.
 */
const CIRCUMFLEX_FOLD = new Map<string, string>([
  ["Â", "A"], ["â", "a"],
  ["Î", "İ"], ["î", "i"],
  ["Û", "U"], ["û", "u"],
]);

/** Şapkalı ünlüleri taban harfe indirir; başka karaktere dokunmaz. */
export function foldCircumflex(input: string): string {
  let out = "";
  for (const ch of input) out += CIRCUMFLEX_FOLD.get(ch) ?? ch;
  return out;
}

/** Türkçe kurallarına göre büyük harfe çevirir (i→İ, ı→I). */
export function toUpperTR(input: string): string {
  let out = "";
  for (const ch of input) {
    out += TO_UPPER.get(ch) ?? ch.toUpperCase();
  }
  return out;
}

/** Türkçe kurallarına göre küçük harfe çevirir (İ→i, I→ı). */
export function toLowerTR(input: string): string {
  let out = "";
  for (const ch of input) {
    out += TO_LOWER.get(ch) ?? ch.toLowerCase();
  }
  return out;
}

/**
 * Kelimeyi oyunun kanonik biçimine getirir: kırpılmış, şapkasız, Türkçe büyük
 * harf. Alfabede olmayan karakterleri ELEMEZ — geçerlilik kontrolü ayrı
 * yapılır, böylece kullanıcıya "geçersiz karakter" geri bildirimi verilebilir.
 *
 * NFC önce geliyor: mobil klavyeler ve kopyalanan metinler "a + birleşen
 * şapka" gibi ayrık diziler üretebiliyor, tablolara bakmadan önce birleşik
 * biçime çevriliyorlar.
 */
export function normalizeWord(input: string): string {
  return toUpperTR(foldCircumflex(input.normalize("NFC").trim()));
}

/** Verilen dizedeki tüm karakterler Türk alfabesinde mi? */
export function isTurkishWord(input: string): boolean {
  if (input.length === 0) return false;
  for (const ch of input) {
    if (!ALPHABET_SET.has(ch)) return false;
  }
  return true;
}

/** Normalize edilmiş kelime, oyun için doğru şekilde mi? (uzunluk + alfabe) */
export function hasValidShape(word: string, length: number): boolean {
  return [...word].length === length && isTurkishWord(word);
}

/**
 * Türkçe alfabetik sıralama karşılaştırıcısı.
 * `Array.prototype.sort` varsayılanı Unicode kod noktasına göre sıralar ve
 * ç/ğ/ı/ö/ş/ü harflerini listenin sonuna atar.
 */
export function compareTR(a: string, b: string): number {
  const ua = toUpperTR(a);
  const ub = toUpperTR(b);
  const len = Math.min(ua.length, ub.length);
  for (let i = 0; i < len; i++) {
    const oa = ORDER.get(ua[i]!) ?? Number.MAX_SAFE_INTEGER;
    const ob = ORDER.get(ub[i]!) ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;
  }
  return ua.length - ub.length;
}
